import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { Header } from '@/header/Header.atom';
import { Footer } from '@/footer/Footer.molecule';
import { fetchEveryoneDisabled, newHeaderExperienceEnabled } from '@/services/systemConfiguration.utilities';
import { setCurrentUser, setDarkMode, setStateParams, setTriggerConfetti } from '@/workbench/workbench.actions';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { open as openSocket } from '@/utilities/socket.utilities';
import {
  sqLicenseManagementStore,
  sqStateSynchronizer,
  sqVantageWorkbookStore,
  sqWorkbenchStore,
  sqWorkbookStore,
} from '@/core/core.stores';
import { getCsrfToken } from '@/utilities/auth.utilities';
import { getPageTheme, getWorkbench } from '@/workbench/workbench.utilities';
import {
  goTo,
  isAIStandalone,
  isMarkdownFileViewer,
  isWorksheet,
  navigateToCorrectErrorPage,
  setCurrentPath,
  wasWorksheet,
} from '@/main/routing.utilities';
import { LicenseExpirationWarning } from '@/licenseManagement/LicenseExpirationWarning';
import { load as loadPlugins } from '@/plugin/plugin.actions';
import { loadHomeScreenAddOnTools } from '@/homescreen/addOnTool.actions';
import { SystemWarning } from '@/systemWarning/SystemWarning.organism';
import { checkLicenseStatus } from '@/licenseManagement/licenseManagement.actions';
import { TabTitle } from '@/core/TabTitle.molecule';
import { initializeRedactionService } from '@/utilities/redaction.utilities';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { onAllServerRequestsCanceled } from '@/services/notifier.service';
import { setupNotifierForWorkbook } from '@/workbook/workbook.actions';
import { setupNotifierForAnnotation } from '@/annotation/annotation.actions';
import { setupNotifierForHomescreen } from '@/homescreen/homescreen.actions';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { cancelAll, forEachPending } from '@/requests/pendingRequests.utilities';
import { cleanup } from '@/trendData/duration.actions';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { isPresentationWorkbookMode, switchModeAndTheme } from '@/utilities/utilities';
import { applyWorkbenchConfigUpgrade } from '@/workbench/workbenchUpgrader.utilities';
import { WORKBENCH_SCHEMA_VERSION } from '@/workbench/workbench.constants';
import { TConductorInstance } from 'react-canvas-confetti/dist/types';
import Fireworks from 'react-canvas-confetti/dist/presets/fireworks';
import { ResizableWindowWrapper } from '@/main/ResizableWindowWrapper';
import { isSystemTest } from '@/core/utilities';
import { fetchHeadlessCaptureMetadata } from '@/utilities/screenshot.utilities';
import { PasswordWarning } from '@/passwordWarning/passwordWarning.organism';
import { useDidMount } from 'rooks';
import { OldLoadingBar } from '@/main/OldLoadingBar.molecule';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { switchLanguage } from '@/utilities/i18n.utilities';
import { VantageConditionMonitorStoppedWarning } from '@/vantage/VantageConditionMonitorStoppedWarning';

/**
 * Wrapper component contains all the components that need to access workbench state.
 * This component takes care of subscribing to websocket channels, setting up state in the stores ...
 */
export const WorkbenchWrapperPage: React.FunctionComponent = () => {
  let confettiTimeOutOne: ReturnType<typeof setTimeout>;
  let confettiTimeOutTwo: ReturnType<typeof setTimeout>;
  let confettiTimeOutThree: ReturnType<typeof setTimeout>;

  const [dataLoaded, setDataLoaded] = useState(false);

  const { pathname } = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const urlSearchParams = useMemo(() => new URLSearchParams(searchParams), [searchParams]);
  const searchParamsAsObject = useMemo(() => Object.fromEntries(urlSearchParams), [urlSearchParams]);

  const notDisplayingPresentationMode = !_.includes(pathname, 'present/');

  const displayLicenseWarning = useFluxPath(sqLicenseManagementStore, (store) => store.displayLicenseWarning);
  const stoppedConditionMonitorState = useFluxPath(
    sqVantageWorkbookStore,
    (store) => store.stoppedConditionMonitorState,
  );

  const conductorRef = useRef<TConductorInstance>(undefined);
  const onConfettiInit = ({ conductor }: { conductor: TConductorInstance }) => {
    conductorRef.current = conductor;
  };

  useEffect(
    () => () => {
      clearTimeout(confettiTimeOutOne);
      clearTimeout(confettiTimeOutTwo);
      clearTimeout(confettiTimeOutThree);
    },
    //useDidMount resulted in odd behavior in system test
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { triggerConfetti, confettiMessageKey, darkMode, interactiveSessionId, userLanguage } = useFluxPaths(
    sqWorkbenchStore,
    ['triggerConfetti', 'confettiMessageKey', 'darkMode', 'interactiveSessionId', 'userLanguage'],
  );
  if (triggerConfetti && !isSystemTest() && !headlessRenderMode()) {
    const conductor = conductorRef.current;
    conductor?.shoot();
    confettiTimeOutOne = setTimeout(() => conductor?.shoot(), 1000);
    confettiTimeOutTwo = setTimeout(() => conductor?.shoot(), 1500);
    confettiTimeOutThree = setTimeout(() => conductor?.shoot(), 2000);
    if (confettiMessageKey) {
      successToast({ messageKey: confettiMessageKey });
    }
    setTriggerConfetti(false, '');
  }

  const {
    isVantage,
    isAnalysis,
    isReportBinder: isTopic,
    isActionBoard,
  } = useFluxPaths(sqWorkbookStore, ['isVantage', 'isAnalysis', 'isReportBinder', 'isActionBoard']);

  useEffect(() => {
    switchModeAndTheme(darkMode && !headlessRenderMode(), getPageTheme(isVantage, isAnalysis, isTopic, isActionBoard));
  }, [isVantage, isAnalysis, isTopic, isActionBoard, darkMode, pathname]);

  useEffect(() => {
    // The order of operations and the actions taken are important for preventing worksteps leaking!
    setCurrentPath(pathname);
    // Dismiss toasts when transitioning from homescreen to worksheet or vice versa
    if (wasWorksheet() !== isWorksheet()) {
      toast.dismiss();
    }

    const newStateParams = { ...params, ...searchParamsAsObject };

    // If we are already transitioning, it is unsafe to start another transition without canceling or
    // otherwise letting the in progress transition finish normally. It isn't safe because loading most
    // states requires asynchronous requests that change global state (the stores), if two transitions are
    // running at the same time then that leads to race conditions concerning the final global state. The
    // only way to effectively cancel the transition is to cause the page to reload. This isn't ideal for
    // the user experience, but it is better than letting state from two worksheets mingle (CRAB-35397).
    if (
      wasWorksheet() &&
      sqStateSynchronizer.unsafeTransition() &&
      sqWorkbenchStore.stateParams.worksheetId !== newStateParams.worksheetId
    ) {
      sqStateSynchronizer
        .checkNavigationIsSafe()
        .then(([finished, pendingRequestsCount, stillHydrating, shouldNavigate]) => {
          const url = _.join(_.compact([pathname, urlSearchParams.toString()]), '?');
          if (!shouldNavigate) {
            console.info(`Stopping navigation to ${url} because the URL changed by the user while rehydrating`);
          } else if (finished) {
            // We can use our router to navigate to the new URL
            goTo(url, true);
          } else {
            // Well, we tried, but it's not safe to navigate yet. We give up and reload the page to prevent state leaks.
            console.info(`Unsafe navigation ${{ pendingRequestsCount, stillHydrating }}. Reloading the page.`);
            forEachPending((id, canceler) => console.info(`Pending request: ${id}`, canceler.config));
            window.location.assign(url);
          }
        });

      return;
    }

    // Prevents reloading when the user double clicks
    if (!_.isEqual(sqWorkbenchStore.stateParams, newStateParams)) {
      if (wasWorksheet()) {
        // The pendingRequestsCount() should ensure that if any requests remain the page is reloaded, but just
        // in case something was not picked up we cancel all outstanding requests that were initiated on the worksheet
        cancelAll().finally(() => {
          // Ensures all websocket subscriptions are closed
          cleanupOnUnload();
          cleanup();
          setStateParams(newStateParams);
        });
      } else {
        setStateParams(newStateParams);
      }
    }
  }, [pathname, params, searchParams, searchParamsAsObject, urlSearchParams]);

  useEffect(() => {
    switchLanguage(userLanguage);
  }, [userLanguage]);

  useDidMount(() => {
    // set up notifiers
    setupNotifierForWorkbook();
    setupNotifierForAnnotation();
    setupNotifierForHomescreen();

    // Notify the user if an admin user cancels all server requests
    onAllServerRequestsCanceled(() => {
      warnToast({ messageKey: 'REQUEST_CANCELLATION.ALL_BY_ADMIN' });
    });

    initializeRedactionService();

    Promise.all([
      // Fetch initial data
      fetchEveryoneDisabled(),
      // setCurrentUser() must be before loading the plugins so admins only all in-development Add-ons
      setCurrentUser()
        .then(() => loadPlugins())
        .then(() => loadHomeScreenAddOnTools())
        .catch(_.noop),
      checkLicenseStatus(pathname),
      // Miscellaneous setup
      fetchHeadlessCaptureMetadata(),
      openSocket(interactiveSessionId, getCsrfToken()),
    ])
      .then(() => {
        const workbenchState = getWorkbench();
        const upgradedState = applyWorkbenchConfigUpgrade(
          workbenchState,
          workbenchState?.stores?.sqWorkbenchStore?.version || 1,
          WORKBENCH_SCHEMA_VERSION,
        );

        sqStateSynchronizer.rehydrate(upgradedState, {
          persistenceLevel: 'WORKBENCH',
          initializeMode: 'SOFT',
        });
      })
      .then(() => {
        const darkModeParam = searchParamsAsObject['darkMode'];
        if (darkModeParam) {
          setDarkMode(darkModeParam === 'enabled' || darkModeParam === 'true');
        }
      })
      .then(() => {
        setDataLoaded(true);
      })
      .catch((error) => {
        navigateToCorrectErrorPage(error);
      });
  });

  const aiStandalone = isAIStandalone();
  const markdownFileViewer = isMarkdownFileViewer();
  const isInAIFrame = typeof window !== 'undefined' && window.self !== window.top && _.includes(pathname, '/ai');

  return (
    <>
      <TabTitle />

      {dataLoaded ? (
        <div className={classNames('flexRowContainer', 'fullViewport', {})} id="mainView" data-testid="mainView">
          {!isVantage && !newHeaderExperienceEnabled() && !headlessRenderMode() && !isPresentationWorkbookMode() ? (
            <OldLoadingBar />
          ) : null}
          {notDisplayingPresentationMode && !isInAIFrame && (
            <>
              {displayLicenseWarning && <LicenseExpirationWarning />}
              <SystemWarning />
              <PasswordWarning />
              {isVantage && stoppedConditionMonitorState && (
                <VantageConditionMonitorStoppedWarning stoppedConditionMonitorState={stoppedConditionMonitorState} />
              )}
            </>
          )}
          {notDisplayingPresentationMode && !aiStandalone && !markdownFileViewer ? <Header /> : null}
          <div className="flexFill flexColumnContainer">
            <ResizableWindowWrapper>
              <Outlet />
            </ResizableWindowWrapper>
          </div>
          {notDisplayingPresentationMode && !aiStandalone ? <Footer /> : null}
        </div>
      ) : null}
      <div className="fireworks">
        <Fireworks onInit={onConfettiInit} />
      </div>
    </>
  );
};
